import React from "react";
import { AlertCircle, RefreshCw } from "lucide-react";

type Props = {
    children: React.ReactNode;
    /** Titel im Fallback (deutschsprachig). */
    title?: string;
    /** Optionaler Test-Id-Präfix. */
    testId?: string;
};

type State = { hasError: boolean; message?: string };

/**
 * Generische ErrorBoundary für Admin-Bereiche. Fängt Render-Fehler ab und
 * ersetzt die CRA-Uncaught-Runtime-Error-Overlay durch eine ruhige Karte
 * inklusive „Erneut laden"-Button.
 *
 * Wichtig: ErrorBoundaries fangen KEINE async Promise-Rejections. Deshalb
 * bleiben zusätzliche try/catch-Wrapper an den einzelnen Fetch-Aufrufen
 * die primäre Verteidigungslinie (siehe UppyUploader.tsx).
 */
export default class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(err: Error): State {
        return { hasError: true, message: err.message };
    }

    componentDidCatch(err: Error, info: React.ErrorInfo) {
        console.warn(
            "[ErrorBoundary] caught",
            err.message,
            info.componentStack,
        );
    }

    private handleReload = () => {
        this.setState({ hasError: false, message: undefined });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div
                    data-testid={this.props.testId || "app-error-boundary"}
                    className="max-w-xl mx-auto my-12 rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900 shadow-sm"
                >
                    <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" aria-hidden />
                        <div className="min-w-0 flex-1">
                            <p className="font-semibold">
                                {this.props.title || "Ein Fehler ist aufgetreten"}
                            </p>
                            <p className="mt-1 text-sm text-amber-800/90 break-words">
                                {this.state.message || "Bitte lade den Bereich erneut."}
                            </p>
                            <button
                                type="button"
                                onClick={this.handleReload}
                                data-testid="app-error-boundary-retry"
                                className="mt-4 inline-flex items-center gap-2 rounded-md bg-amber-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-800"
                            >
                                <RefreshCw className="h-4 w-4" aria-hidden />
                                Erneut versuchen
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
